import React, { useMemo } from 'react';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import type { ImmutableRootState, ImmutableEntity, ReferenceField } from '@uncinc/react-drupal-core/types';
import type { ParagraphPhaseProps } from '../implementations/Paragraph';

interface Submissions {
  [key: string]: Object[]
}

interface Props {
  phases?: Immutable.List<ImmutableEntity<ReferenceField<ParagraphPhaseProps>>>;
}

const useProgramCompletion = ({ phases: phasesOverride }: Props = {}) => {
  // Get phases from Redux if not provided as a parameter
  const phasesFromRedux = useSelector<
  ImmutableRootState<any>,
  Immutable.List<ImmutableEntity<ReferenceField<ParagraphPhaseProps>>>
  >(
    (state) => {
      const programNode = state.getIn(['node'], Immutable.Map())
        .find((node: any) => node.getIn(['type', 0, 'target_id']) === 'program');
      return programNode ? programNode.get('field_paragraphs', Immutable.List()) : Immutable.List();
    },
  );

  const phases = phasesOverride || phasesFromRedux;

  const completedExercises = useSelector<ImmutableRootState<Submissions>, Submissions>(
    (state) => state.getIn(['exerciseSubmissions', 'submissions'], {}),
  );

  const isProgramCompleted = useMemo(() => {
    if (phases.isEmpty?.()) {
      return false;
    }

    const exerciseTitles = phases.map((phase) => {
      const exercisesInPhase = phase.getIn(['entity', 'field_exercise'], Immutable.List());
      return exercisesInPhase.map((exercise) => {
        const webform = exercise.getIn(['entity', 'field_webform', 0, 'target_id'], null);
        return webform;
      });
    }).flatten(1);

    // ensure all items in exerciseTitles has a key in completedExercises
    return exerciseTitles.every((title: string) => !!completedExercises[title]);
  }, [phases, completedExercises]);

  return {
    isProgramCompleted,
  };
};

export default useProgramCompletion;
